/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { useCallback, useEffect, useState } from "react";

import { usePlayerTranslation } from "../../../../../../shared/context/PlayerTranslationContext";
import {
  PlayerSubtitleTrack,
  usePlayerController,
} from "../../../../../BasePlayer";
import { useToggleVisibility } from "../../../../Base";

import { useAvailableSection } from "./AvailableSectionContext";

export const useSubtitleSettings = () => {
  const { player } = usePlayerController();
  const { toggleVisibility } = useToggleVisibility();
  const { setAvailableStatus } = useAvailableSection();

  const { t } = usePlayerTranslation();

  const DISABLED_TRACK: PlayerSubtitleTrack = {
    id: "disabled",
    label: t("PLAYER_DISABLED_SUBTITLES_TRACK"),
    language: "",
    active: false,
  };

  const [tracks, setTracks] = useState<PlayerSubtitleTrack[]>([]);

  const [activeTrack, setActiveTrack] =
    useState<PlayerSubtitleTrack>(DISABLED_TRACK);

  const updateSubtitles = useCallback(() => {
    const subtitleTracks = player.subtitleTracks();
    const selectedTrack = subtitleTracks.find((track) => track.active);

    if (subtitleTracks.length) {
      setAvailableStatus("subtitles", true);
    }

    setTracks([DISABLED_TRACK, ...subtitleTracks]);
    setActiveTrack(selectedTrack ? selectedTrack : DISABLED_TRACK);
  }, [player, setAvailableStatus]);

  useEffect(() => {
    updateSubtitles();

    const unsubscribe = player.onSubtitleTrackChange(() => updateSubtitles());

    return () => unsubscribe();
  }, [player, setAvailableStatus, updateSubtitles]);

  const onTrackSelect = useCallback(
    (track: PlayerSubtitleTrack) => {
      player.setSubtitleTrack(track.id);
      toggleVisibility();
    },
    [player, toggleVisibility],
  );

  return {
    tracks,
    activeTrack,
    onTrackSelect,
  };
};
